import React, { useState, useEffect, SyntheticEvent } from 'react';

import { ILink, ILinkFields } from '@/types/contentful';

import { usePageDataContext } from '../../../../context/PageData';
import { useCookie } from '../../../../hooks/useCookie';

import {
  readOnetrustSettings,
  CONSENT_COOKIE_KEY,
  CONSENT_OPT_IN,
  CONSENT_OPT_OUT
} from '../../../../utils/analytics/onetrust';
import { trackWithFingerprint } from '../../../../utils/trackWithFingerprint';

import { ConsentModal } from '../../../ConsentModal';
import Link from '../../../Link';

interface LegalNavLinksProps {
  isAffiliateFooter?: boolean;
  links?: ILink[];
  className?: string;
}

export function LegalNavLinks({
  isAffiliateFooter,
  links,
  className,
  ...props
}: LegalNavLinksProps) {
  const { cookies: { [CONSENT_COOKIE_KEY]: pvcyCookie } = {} } =
    usePageDataContext();

  // Handle consent cookie default from server
  const pvcySettings = readOnetrustSettings(pvcyCookie);

  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const [optedOut, setOptedOut] = useState<boolean>(
    pvcySettings?.C0004 === false
  );

  const [pCookie, updateCookie] = useCookie(CONSENT_COOKIE_KEY);

  useEffect(() => {
    // Update consent cookie when toggle changes
    const settings = readOnetrustSettings(pCookie);
    setOptedOut(settings?.C0004 === false);
  }, [pCookie]);

  const updatePrivacyStatus = () => {
    const expiryDate = new Date();
    expiryDate.setMonth(expiryDate.getMonth() + 12);

    const pvcy = !optedOut ? CONSENT_OPT_IN : CONSENT_OPT_OUT;

    updateCookie(pvcy, { expires: expiryDate });
  };

  const toggleOptOutModal = () => {
    if (isConsentModalOpen === false) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = '';
    }

    // Update privacy cookie and toggle modal
    updatePrivacyStatus();
    setIsConsentModalOpen(!isConsentModalOpen);
  };

  return (
    <div
      className={`flex flex-col ${className || ''}`}
      data-testid="legal-nav-list"
      {...props}
    >
      <ul className="-mx-2 list-none p-0">
        {links &&
          links.map(({ sys, fields }) => {
            const { title, url, openInNewTab, hasAffiliateLink }: ILinkFields =
              fields;
            const path = url ?? '';

            return title?.toLowerCase().includes('do not sell') ? (
              <li
                className="mb-3 block px-2 desktop:mb-1 desktop:inline-block desktop:border-r desktop:border-r-[#757575] desktop:last:border-r-0"
                data-testid="privacy-modal-link"
                key={sys?.id || title}
              >
                <Link
                  className="typography-body-2 font-medium leading-4 text-on-screen-surface-dark no-underline transition-colors duration-200 hover:text-[#00D9B4] hover:underline"
                  href="#"
                  onClick={(e: SyntheticEvent) => {
                    e.preventDefault();
                    trackWithFingerprint('Do Not Sell Selected', {
                      opted_out: optedOut
                    });
                    toggleOptOutModal();
                  }}
                >
                  {title}
                </Link>
              </li>
            ) : (
              <li
                key={sys?.id || title}
                className="mb-3 block px-2 desktop:mb-1 desktop:inline-block desktop:border-r desktop:border-r-[#757575] desktop:last:border-r-0"
              >
                <Link
                  className="typography-body-2 font-medium leading-4 text-on-screen-surface-dark no-underline transition-colors duration-200 hover:text-[#00D9B4] hover:underline"
                  href={
                    isAffiliateFooter && hasAffiliateLink
                      ? `/affiliates${path}`
                      : path
                  }
                  target={openInNewTab ? '_blank' : ''}
                  rel="noopener noreferrer"
                >
                  {title}
                </Link>
              </li>
            );
          })}
      </ul>
      {isConsentModalOpen && (
        <ConsentModal
          optedOut={optedOut}
          setOptedOut={setOptedOut}
          toggleOptOutModal={toggleOptOutModal}
        />
      )}
    </div>
  );
}
